import {
  COLUMN_LINKED_WORK_ID,
  COLUMN_LINKED_WORK_JIRA_CUSTOM_FIELD,
  COLUMN_LINKED_WORK_STATUS,
  COLUMN_LINKED_WORK_TITLE,
  COLUMN_NAMES,
} from 'constants/filters';

/**
 * Dynamic linked work column helpers
 * Note: These functions take 0-based index but generate 1-based column names
 * to match the backend CSV export format (linked_work_id_1, linked_work_id_2, etc.)
 */
export const getLinkedWorkIdColumn = (index: number) => `linked_work_id_${index + 1}`;
export const getLinkedWorkTitleColumn = (index: number) => `linked_work_title_${index + 1}`;
export const getLinkedWorkStatusColumn = (index: number) => `linked_work_status_${index + 1}`;
export const getLinkedWorkJiraCustomFieldColumn = (index: number) =>
  `linked_work_jira_custom_field_${index + 1}`;

export const isLinkedWorkColumn = (columnId: string): boolean =>
  columnId.startsWith('linked_work_id_') ||
  columnId.startsWith('linked_work_title_') ||
  columnId.startsWith('linked_work_status_') ||
  columnId.startsWith('linked_work_jira_custom_field_');

/**
 * Returns the 0-based index from a 1-based column name
 */
export const getLinkedWorkColumnIndex = (columnId: string): number | null => {
  const match = columnId.match(/linked_work_(?:id|title|status|jira_custom_field)_(\d+)$/);
  return match ? parseInt(match[1], 10) - 1 : null;
};

export const getLinkedWorkParentColumn = (columnId: string): string | null => {
  // All four linked-work prefixes are mutually distinct, so check order does not matter.
  if (columnId.startsWith('linked_work_jira_custom_field_'))
    return COLUMN_LINKED_WORK_JIRA_CUSTOM_FIELD;
  if (columnId.startsWith('linked_work_id_')) return COLUMN_LINKED_WORK_ID;
  if (columnId.startsWith('linked_work_title_')) return COLUMN_LINKED_WORK_TITLE;
  if (columnId.startsWith('linked_work_status_')) return COLUMN_LINKED_WORK_STATUS;
  return null;
};

/**
 * Gets display name for a dynamic linked work column
 */
export const getLinkedWorkColumnName = (columnId: string): string | undefined => {
  const index = getLinkedWorkColumnIndex(columnId);
  if (index !== null) {
    const itemNumber = index + 1;
    if (columnId.includes('_jira_custom_field_')) return `Jira custom field ${itemNumber}`;
    if (columnId.includes('_id_')) return `Linked work item ${itemNumber} ID`;
    if (columnId.includes('_title_')) return `Linked work item ${itemNumber} title`;
    if (columnId.includes('_status_')) return `Linked work item ${itemNumber} sync status`;
  }
  return undefined;
};

export const getColumnName = (columnId: string): string | undefined => {
  if (columnId in COLUMN_NAMES) {
    return COLUMN_NAMES[columnId as keyof typeof COLUMN_NAMES];
  }

  return getLinkedWorkColumnName(columnId);
};
